// useTestMailHook.ts
import { useState } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "react-router-dom";
import { sendEmails } from "@/lib/api/handlers/AWSHandling";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import {
  suppressedRecipientsMessage,
  suppressedRecipientsOf,
} from "@/lib/errors/suppressedRecipients";

export const useTestMail = () => {
  const tEmailAppbar = useTranslations("emailAppbar");
  const tErrors = useTranslations("errors");
  const params = useParams();
  const eventId = params?.eventId;

  const [testMailValue, setTestMailValue] = useState("");
  const [testMailError, setTestMailError] = useState<string | undefined>();
  const [testMailLoading, setTestMailLoading] = useState(false);

  /**
   * Validates email addresses for test mail
   * @param value - Comma-separated email addresses
   * @returns Error message or undefined if valid
   */
  const validateTestEmails = (value: string): string | undefined => {
    const trimmedValue = value.trim();

    // Check if empty
    if (!trimmedValue) {
      return tEmailAppbar("validationErrorEmpty");
    }

    // Split by comma and clean up
    const emails = trimmedValue
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    // Check if we have at least one email after filtering
    if (emails.length === 0) {
      return tEmailAppbar("validationErrorEmpty");
    }

    // Check for too many emails
    if (emails.length > 5) {
      return tEmailAppbar("validationErrorTooMany");
    }

    // Check for duplicates (case-insensitive)
    const emailsLowerCase = emails.map((email) => email.toLowerCase());
    const uniqueEmails = new Set(emailsLowerCase);
    if (uniqueEmails.size !== emails.length) {
      return tEmailAppbar("validationErrorDuplicates");
    }

    // Validate each email format
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmails = emails.filter((email) => {
      // Check basic regex
      if (!emailRegex.test(email)) return true;

      // Additional checks
      const [localPart, domain] = email.split("@");

      // Local part shouldn't start or end with a dot
      if (localPart.startsWith(".") || localPart.endsWith(".")) return true;

      // Domain shouldn't start or end with a dot or hyphen
      if (
        domain.startsWith(".") ||
        domain.endsWith(".") ||
        domain.startsWith("-") ||
        domain.endsWith("-")
      )
        return true;

      // No consecutive dots
      if (email.includes("..")) return true;

      return false;
    });

    if (invalidEmails.length > 0) {
      return `${tEmailAppbar("validationErrorInvalid")}${invalidEmails.join(
        ", "
      )}`;
    }

    return undefined;
  };

  /**
   * Handles input change and clears validation error
   */
  const handleTestMailChange = (value: string) => {
    setTestMailValue(value);
    // Clear validation error when user types
    if (testMailError) {
      setTestMailError(undefined);
    }
  };

  /**
   * Handles sending test emails with validation
   */
  const handleSendTestMail = async (
    emailTemplateId: string,
    primaryLanguage: string,
    sampleGuestId?: string
  ) => {
    setTestMailLoading(true);
    const error = validateTestEmails(testMailValue);
    if (error) {
      setTestMailLoading(false);
      setTestMailError(error);
      return;
    }

    // Clear error and proceed with sending
    setTestMailError(undefined);

    const response = await sendEmails(
      eventId as string,
      emailTemplateId,
      primaryLanguage,
      testMailValue.split(",").map((email) => email.trim()),
      sampleGuestId
    );

    // Check if response is a ServerError by verifying it's an object first
    if (response && typeof response === "object" && "isServerError" in response) {
      // Translated, not the raw code: the backend dedups one test mail per
      // (event, template, address) and minute, and stepping through preview
      // guests invites a second send inside that window — which used to print
      // the literal "rateLimited" under the input.
      // Kanzlei P5: a suppression refusal names the listed addresses.
      const code = (response as { message?: unknown }).message;
      const listed = suppressedRecipientsMessage(tEmailAppbar, code, suppressedRecipientsOf(response));
      // A 429 with Retry-After says how long to wait, like PTestMail does —
      // the suppression probe brake (10 checked addresses per minute) makes
      // this reachable from a normal dialog.
      const retryAfter = (response as { retryAfter?: unknown }).retryAfter;
      const wait =
        code === "rateLimited" && typeof retryAfter === "number" && retryAfter > 0
          ? tErrors("rateLimitedCountdown", { seconds: Math.ceil(retryAfter) })
          : undefined;
      setTestMailError(listed ?? wait ?? translateBackendError(tErrors, response));
      setTestMailLoading(false);
      return;
    }

    setTestMailLoading(false);
    // Optionally clear the test mail input after successful send
    setTestMailValue("");
  };

  return {
    testMailValue,
    testMailError,
    testMailLoading,
    handleTestMailChange,
    handleSendTestMail,
  };
};
